import { mapActions } from "vuex";

export const synchronization = {
  methods: {
    ...mapActions({
      getLocalConnectedUser: "auth/getLocalConnectedUser",
      getProject: "projects/getProject",
      getPatient: "patients/getPatient",

      createPatient: "patients/createPatient",
      createProject: "projects/createProject",
      updateProject: "projects/updateProject",

      // Products
      createMinerva: "minerva/createMinerva",
      getMinerva: "minerva/getMinerva",
      createMoldedSeat: "moldedSeat/createMoldedSeat",
      getMoldedSeat: "moldedSeat/getMoldedSeat",
      createOtn: "otn/createOtn",
      getOtn: "otn/getOtn",
      createLift: "lift/createLift",
      getLift: "lift/getLift",
      createRetouchingRepair: "retouchingRepair/createRetouchingRepair",
      getRetouchingRepair: "retouchingRepair/getRetouchingRepair",
      createSeat: "seat/createSeat",
      getSeat: "seat/getSeat",
      createPositioningSeat: "positioningSeat/createPositioningSeat",
      getPositioningSeat: "positioningSeat/getPositioningSeat",
      createUpperLimb: "upperLimb/createUpperLimb",
      getUpperLimb: "upperLimb/getUpperLimb",
      createLowerLimb: "lowerLimb/createLowerLimb",
      getLowerLimb: "lowerLimb/getLowerLimb",
      createCorset: "corset/createCorset",
      getCorset: "corset/getCorset"
    }),

    // Returns object literal
    products() {
      return {
        NECK_BRACE: {
          table: this.$db.minerva,
          get: this.getMinerva,
          create: this.createMinerva
        },
        MOLDED_SEAT: {
          table: this.$db.molded_seat,
          get: this.getMoldedSeat,
          create: this.createMoldedSeat
        },
        OTN: {
          table: this.$db.otn,
          get: this.getOtn,
          create: this.createOtn
        },
        LIFT: {
          table: this.$db.lift,
          get: this.getLift,
          create: this.createLift
        },
        RETOUCHING_REPAIR: {
          table: this.$db.retouching_repair,
          get: this.getRetouchingRepair,
          create: this.createRetouchingRepair
        },
        SEAT: {
          table: this.$db.seat,
          get: this.getSeat,
          create: this.createSeat
        },
        POSITIONING_SEAT: {
          table: this.$db.positioning_seat,
          get: this.getPositioningSeat,
          create: this.createPositioningSeat
        },
        UPPER_LIMB_ORTHOSIS: {
          table: this.$db.upper_limb,
          get: this.getUpperLimb,
          create: this.createUpperLimb
        },
        LOWER_LIMB_ORTHOSIS: {
          table: this.$db.lower_limb,
          get: this.getLowerLimb,
          create: this.createLowerLimb
        },
        CORSET: {
          table: this.$db.corset,
          get: this.getCorset,
          create: this.createCorset
        }
      };
    },

    // Synchronizes project/product with API
    async synchronize(project) {
      try {
        // Checks if projecy exist in API, otherwise create it
        const projectExist = await this.projectExist(project.uuid);

        if (!projectExist) {
          await this.newProject(project);
        } else {
          await this.existingProject(project);
        }

        // Retrieves updated project from API
        project = await this.getProject(project.uuid);

        // Add local properties
        project.updated = null;
        project.local_version = null;
        project.server_version = project.product_uuid;
        project.transferable = false;

        // Updated local project
        await this.$db.projects.put(project);
      } catch (error) {
        console.log(error);
      }
    },

    // Synchronizes existing porject
    async existingProject(project) {
      try {
        // Checks if project have a local version of a product
        if (project.local_version) {
          // If so, send the product to API
          const product = await this.products()[
            project.product_model
          ].table.get(project.local_version);
          // Gets connected user info
          const user = await this.getLocalConnectedUser();

          // Add user_id property to product object
          product.user_id = user.id;
          await this.products()[project.product_model].create(product);
        }

        // Remove useless properties
        delete project.local_version;
        delete project.server_version;
        delete project.updated;

        // Update Project
        await this.updateProject({
          uuid: project.uuid,
          payload: project
        });
      } catch (error) {
        console.log("Synchronization.js Method: existingProject: ", error);
      }
    },

    // Synchronizes new project
    async newProject(project) {
      try {
        // Gets local patient
        const patient = await this.$db.patients.get(project.patient_uuid);

        await this.createPatient(patient);
        await this.createProject(project);
        const product = await this.products()[project.product_model].table.get(
          project.product_uuid
        );
        // Gets connected user info
        const user = await this.getLocalConnectedUser();

        // Add user_id property to product object
        product.user_id = user.id;
        await this.products()[project.product_model].create(product);
      } catch (error) {
        console.log("Synchronization.js Method: newProject: ", error);
      }
    },

    // Checks if given project exist in API
    async projectExist(projectUuid) {
      try {
        return await this.getProject(projectUuid);
      } catch (error) {
        if (error.response.status === 404) {
          return false;
        }
      }
    },

    //
    // Gets projects/products
    //

    // Synchronizes projects in IndexedDB
    async synchronizeProjects(projects_array, synchronize_all = false) {
      // Gets products object literals
      const products = this.products();

      if (!synchronize_all) {
        // Deletes all projects that are no longer selected
        try {
          // Gets projects primary keys from indexedDB
          const projects = await this.$db.projects.toCollection().keys();

          // Gets deselected projects
          const deselectedProjects = projects.filter(
            item => projects_array.indexOf(item) == -1
          );

          deselectedProjects.forEach(async project_uuid => {
            // Gets project from IndexedDB
            const project = await this.$db.projects.get(project_uuid);

            // Deletes project from IndexedDB
            await this.$db.projects.delete(project.uuid);

            // Deletes product from IndexedDB
            await products[project.product_model].table.delete(
              project.product_uuid
            );
          });
        } catch (error) {
          throw error;
        }
      }

      // Iterate through projects
      projects_array.forEach(async project_uuid => {
        // Checks if project exist in IndexedDB
        const projectExist = await this.$db.projects.get(project_uuid);
        if (projectExist !== undefined) return;

        try {
          // Gets project from API
          const project = await this.getProject(project_uuid);

          // Gets product from API
          const product = await products[project.product_model].get(
            project.product_uuid
          );

          // Save project and product in IndexedDB
          await this.saveProjectProduct(project, product);
        } catch (error) {
          throw error;
        }
      });
    },

    // Saves a project and a product in IndexedDB
    async saveProjectProduct(project, product) {
      // Gets products object literals
      const products = this.products();

      // Add versions properties to project object
      project.server_version = project.product_uuid;
      project.local_version = null;

      try {
        // Adds project to IndexedDB
        await this.$db.projects.add(project);

        // Adds product to IndexedDB
        await products[project.product_model].table.add(product);
      } catch (error) {
        console.error("Deleting project and product !", error);

        // Deletes project from IndexedDB
        await this.$db.projects.delete(project.uuid);

        // Deletes product from IndexedDB
        await products[project.product_model].table.delete(product.uuid);

        throw error;
      }
    }
  }
};
